import { type FC, useCallback, useMemo, useRef, useState } from "react";

import Spinner from "~/common/components/Spinner/Spinner";
import OrderbookSearchBox, {
  type OrderbookSearchBoxData,
  type OrderbookSearchBoxHandle,
} from "~/features/orderbook/components/OrderbookSearchBox/OrderbookSearchBox";
import OrderbookResultsTable from "~/features/orderbook/components/OrderbookResultsTable";
import { type OrderbookRowItem } from "~/features/orderbook/models/orderbookModels";
import { DownloadReportButtons } from "~/features/orders/common/components/DownloadReportButtons";
import { type ReportType } from "~/features/orderbook/api/downloadOrderbookReport";
import { downloadOrderbook, filterOrderbookRows } from "~/features/orderbook/orderbookFilters";
import { getOrderbookDataResults } from "~/features/orders/common/DownloadOrderHistoryService/DownloadOrderHistoryService";
import { useSearchSubscription } from "~/features/orders/common/hooks/useSearchSubscription";
import { MultiAccountSearchDataProvider } from "~/features/orders/orderList/components/OrderListSearchBox/MultiAccountSelect/MultiAccountSearchDataProvider";
import { useTranslation } from "react-i18next";
import { Alert } from "~/common/mui/Alert/Alert";
import { PageElements } from "~/common/components/PageElements";
import { Stack } from "~/common/mui";
import { OrderQuoteNoResults } from "~/common/components/NoResultsSection/OrderQuoteNoResults";

const OrderbookPage: FC = () => {
  const { t } = useTranslation();

  const [rows, setRows] = useState<OrderbookRowItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [lastSearchQuery, setLastSearchQuery] = useState<OrderbookSearchBoxData>();
  const searchBoxRef = useRef<OrderbookSearchBoxHandle>(null);
  const webservicesDisabled = window.SCHA.config.disableAllWS;

  const search = useSearchSubscription(getOrderbookDataResults, {
    onBegin: () => {
      setIsLoading(true);
      setIsError(false);
      setRows([]);
    },
    onError: () => {
      setIsLoading(false);
      setIsError(true);
    },
    onNext: ({ orderbookData }) => setRows((prev) => prev.concat(orderbookData)),
    onComplete: () => setIsLoading(false),
  });

  const onSubmitHandler = useCallback(
    (formData: OrderbookSearchBoxData) => {
      setLastSearchQuery(formData);
      search(formData);
    },
    [search],
  );

  const filteredRows = useMemo(() => filterOrderbookRows(rows, lastSearchQuery), [rows, lastSearchQuery]);

  const newResultCount = filteredRows.filter((row) => row.isNew || row.isUpdated).length;

  const showRows = !isError && filteredRows.length > 0;
  const disableInteractions = isLoading || webservicesDisabled;

  return (
    <Stack gap={3}>
      <Stack direction={"row"} sx={{ gap: 2, alignItems: "center" }}>
        <PageElements.Title>{t("page.abmonitor.title")}</PageElements.Title>
        <PageElements.Subtitle>
          <span>{t("page.abmonitor.resultTitle.total", { count: filteredRows.length })}</span>
          <span> / </span>
          <span>{t("page.abmonitor.resultTitle.new", { count: newResultCount })}</span>
        </PageElements.Subtitle>
      </Stack>

      {isLoading && <Spinner />}

      <OrderbookSearchBox ref={searchBoxRef} onSubmit={onSubmitHandler} disabled={disableInteractions}>
        <DownloadReportButtons
          onDownload={(type) => downloadOrderbook(type as unknown as ReportType, lastSearchQuery)()}
          disabled={disableInteractions}
          testIdPrefix="abmonitor"
          label={t("page.abmonitor.downloads.title")}
        />
      </OrderbookSearchBox>

      {showRows && <OrderbookResultsTable rows={filteredRows} disableSorting={disableInteractions} />}

      {!webservicesDisabled && !isLoading && isError && <Alert severity="error">{t("page.abmonitor.error")}</Alert>}

      {!isLoading && !isError && !filteredRows.length && (
        <OrderQuoteNoResults
          title={t("page.orderbook.noResults.title")}
          onReset={() => searchBoxRef.current?.reset()}
        />
      )}
    </Stack>
  );
};

export function createOrderbookPage(data: { accounts: string; showUnconfirmedLinesFilter: boolean }) {
  return (
    <MultiAccountSearchDataProvider
      accountsJson={data.accounts}
      showUnconfirmedLinesFilter={data.showUnconfirmedLinesFilter}
    >
      <OrderbookPage />
    </MultiAccountSearchDataProvider>
  );
}
